import { useEffect, useMemo, useState } from 'react';
import type { JourneySetupResponse, MilestoneInput, MilestoneRole, MilestoneTrigger } from '@coachrex/shared';
import { useJourneySetup, useMe, useAiMilestones, useSaveJourney } from '../lib/api';
import { AiHeardList } from '../components/AiHeard';
import { ErrorBox, PageHeader, Segmented, Skeleton } from '../components/ui';
import { useToast } from '../components/Toast';
import './journeySetup.css';

type Draft = MilestoneInput & { key: string; leads: number | null };
type Rule = JourneySetupResponse['recoveryRule'];

let seq = 0;
const newKey = () => `new-${++seq}`;
const slug = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Handoff screen 10: milestones, not pipeline stages. */
export function JourneySetup() {
  const { data, error, isLoading } = useJourneySetup();
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="page">
        <Skeleton height={60} />
        <Skeleton height={420} />
      </div>
    );
  }
  return <Editor data={data} />;
}

function fromServer(data: JourneySetupResponse): Draft[] {
  return data.milestones.map((m) => ({ ...m, key: m.id, leads: m.leads }));
}

function Editor({ data }: { data: JourneySetupResponse }) {
  const save = useSaveJourney();
  const toast = useToast();
  const [list, setList] = useState<Draft[]>(() => fromServer(data));
  const [rule, setRule] = useState<Rule>(data.recoveryRule);
  const [sel, setSel] = useState(0);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) {
      setList(fromServer(data));
      setRule(data.recoveryRule);
    }
  }, [data, dirty]);

  const cur = list[Math.min(sel, list.length - 1)];
  const touch = (next: Draft[]) => {
    setList(next);
    setDirty(true);
  };
  const patch = (p: Partial<Draft>) => {
    if (!cur) return;
    let next = list.map((m) => (m.key === cur.key ? { ...m, ...p } : m));
    // Only one Conversion and one Goal: setting one clears it elsewhere.
    if (p.role) next = next.map((m) => (m.key !== cur.key && m.role === p.role ? { ...m, role: null } : m));
    touch(next);
  };
  const move = (dir: -1 | 1) => {
    const i = list.findIndex((m) => m.key === cur?.key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j]!, next[i]!];
    touch(next);
    setSel(j);
  };
  const add = () => {
    touch([...list, { key: newKey(), name: 'New milestone', trigger: 'tag', value: '', listenFor: null, optional: false, role: null, leads: null }]);
    setSel(list.length);
  };
  const remove = () => {
    if (!cur || list.length <= 1) return;
    if (!confirm(`Remove "${cur.name}"? Its history is deleted too.`)) return;
    touch(list.filter((m) => m.key !== cur.key));
    setSel(Math.max(0, sel - 1));
  };
  const applyTemplate = (name: string) => {
    if (!confirm(`Replace your milestones with the ${name} template? Counts are recalculated from your HighLevel tags and appointments.`)) return;
    touch(data.templates[name]!.map((m) => ({ ...m, key: newKey(), leads: null })));
    setSel(0);
  };

  const problems = list.flatMap((m) =>
    !m.name.trim() ? [`A milestone needs a name.`] : m.trigger !== 'ai' && !m.value ? [`Pick a ${m.trigger === 'tag' ? 'tag' : 'calendar'} for "${m.name}".`] : m.trigger === 'ai' && !m.listenFor?.trim() ? [`Say what the AI should listen for in "${m.name}".`] : [],
  );
  const onSave = () =>
    save.mutate(
      { recoveryRule: rule, milestones: list.map(({ key: _k, leads: _l, ...m }) => ({ ...m, id: m.id, name: m.name.trim() })) },
      {
        onSuccess: (r) => {
          setDirty(false);
          toast({ text: r.reevaluated ? `Saved. Recounted ${r.reevaluated} milestone${r.reevaluated === 1 ? '' : 's'} from your HighLevel history.` : 'Saved' });
        },
      },
    );

  const required = list.filter((m) => !m.optional);
  const target = rule === 'goal' ? list.find((m) => m.role === 'goal') : rule === 'conversion' ? list.find((m) => m.role === 'conversion') : null;

  return (
    <div className="page">
      <PageHeader
        title="Journey setup"
        subtitle="The milestones a lead goes through at your company. A milestone counts the first time it happens and stays reached, even if the tag is removed later."
        actions={
          <button className="btn btn--primary" onClick={add} disabled={list.length >= 15}>
            Add milestone
          </button>
        }
      />
      <div className="tpl-row">
        <span className="card-meta">Start from</span>
        {Object.keys(data.templates).map((t) => (
          <button key={t} className="pill" onClick={() => applyTemplate(t)}>
            {t}
          </button>
        ))}
      </div>

      <div className="js-cols">
        <section className="card js-steps">
          {list.map((m, i) => (
            <button key={m.key} className={`js-step${m.key === cur?.key ? ' is-selected' : ''}`} onClick={() => setSel(i)}>
              <span className={`js-num${m.key === cur?.key ? ' is-accent' : ''}${m.role === 'goal' ? ' is-goal' : ''}${m.optional ? ' is-optional' : ''}`}>{i + 1}</span>
              <span className="js-step__text">
                <span className="js-step__name">
                  {m.name || 'Untitled'}
                  {m.optional && <span className="js-badge">Optional</span>}
                  {m.role === 'conversion' && <span className="js-badge is-accent">Conversion</span>}
                  {m.role === 'goal' && <span className="js-badge is-green">Goal</span>}
                </span>
                <span className="card-meta">{triggerLine(m, data)}</span>
              </span>
              <span className="card-meta" style={{ whiteSpace: 'nowrap' }}>
                {m.leads == null ? 'New' : `${m.leads.toLocaleString('en-US')} leads`}
              </span>
            </button>
          ))}
        </section>

        {cur && (
          <section className="card stack-14">
            <div className="card-head" style={{ alignItems: 'center' }}>
              <span className="overline">
                Milestone {list.findIndex((m) => m.key === cur.key) + 1} of {list.length}
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn--secondary btn--sm" aria-label="Move up" disabled={sel === 0} onClick={() => move(-1)}>
                  ↑
                </button>
                <button className="btn btn--secondary btn--sm" aria-label="Move down" disabled={sel >= list.length - 1} onClick={() => move(1)}>
                  ↓
                </button>
              </span>
            </div>
            <label className="form-label">
              <span>Name</span>
              <input className="text-input" value={cur.name} onChange={(e) => patch({ name: e.target.value })} />
            </label>
            <div className="form-label">
              <span>Counts as reached when</span>
              <TriggerCard t="tag" cur={cur} onPick={(t) => patch({ trigger: t, value: '' })} title="A HighLevel tag is added" help="Best for steps your team or automations already tag.">
<TagPicker value={cur.value} tags={data.tags} onChange={(v) => patch({ value: v })} />
              </TriggerCard>
              <TriggerCard t="appointment" cur={cur} onPick={(t) => patch({ trigger: t, value: '' })} title="An appointment is marked showed" help="Best for meetings, like discovery calls or site visits. Counts on “showed”, not on booked.">
                <select className="text-input" value={cur.value} onChange={(e) => patch({ value: e.target.value })}>
                  <option value="">Pick a calendar…</option>
                  {data.calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </TriggerCard>
              <TriggerCard
                t="ai"
                cur={cur}
                onPick={(t) => patch({ trigger: t, value: slug(cur.name), listenFor: cur.listenFor ?? '' })}
                title="The AI hears it"
                help="For moments nobody tags, like “asked about financing”."
              >
                <textarea className="text-input" rows={2} placeholder="What should the AI listen for?" value={cur.listenFor ?? ''} onChange={(e) => patch({ listenFor: e.target.value })} />
                <span className="card-meta">
                  The AI listens on calls, texts and emails as it grades leads and counts the milestone when it hears it, with the quote shown on the
                  lead. Leads with the HighLevel tag <b>{cur.value || slug(cur.name)}</b> count too.
                </span>
              </TriggerCard>
            </div>
            <div className="js-row">
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Optional</span>
                <span className="card-meta">Shows on the journey, left out of the funnel.</span>
              </span>
              <button className={`toggle${cur.optional ? ' is-on' : ''}`} role="switch" aria-checked={cur.optional} aria-label="Optional" onClick={() => patch({ optional: !cur.optional, role: cur.optional ? cur.role : null })} />
            </div>
            <div className="js-row">
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Use in reports as</span>
                <span className="card-meta">One Conversion and one Goal per workspace. Conversion counts as “booked”.</span>
              </span>
              {cur.optional ? (
                <span className="card-meta">Not for optional milestones</span>
              ) : (
                <Segmented<'step' | 'conversion' | 'goal'>
                  label="Use in reports as"
                  options={[
                    { value: 'step', label: 'Step' },
                    { value: 'conversion', label: 'Conversion' },
                    { value: 'goal', label: 'Goal' },
                  ]}
                  value={cur.role ?? 'step'}
                  onChange={(v) => patch({ role: v === 'step' ? null : (v as MilestoneRole) })}
                />
              )}
            </div>
            <button className="link-btn" style={{ color: 'var(--red-fg)', alignSelf: 'flex-start' }} disabled={list.length <= 1} onClick={remove}>
              Remove milestone
            </button>
          </section>
        )}
      </div>

      <div className="js-cols">
        <Funnel list={list} />
        <section className="card stack-12">
          <span className="card-title">Rescue rules</span>
          <div className="js-row">
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Recovered when the lead reaches</span>
              <span className="card-meta">
                {rule === 'next'
                  ? 'Any milestone after it was flagged.'
                  : target
                    ? `“${target.name}”${rule === 'conversion' ? ' or a later step' : ''}.`
                    : `No ${rule} milestone set. Any milestone counts until you set one.`}
              </span>
            </span>
            <Segmented<Rule>
              label="Recovered when"
              options={[
                { value: 'next', label: 'Next milestone' },
                { value: 'conversion', label: 'Conversion' },
                { value: 'goal', label: 'Goal' },
              ]}
              value={rule}
              onChange={(v) => {
                setRule(v);
                setDirty(true);
              }}
            />
          </div>
          <Static label="Attribution window" value={`${data.attributionDays} days after the lead was flagged`} />
          <Static label="Credit" value="The rep whose call, text or email moved the lead out of Open; otherwise self-recovered." />
          <Static label="Mark as lost" value="After 2 returns to Open with no reply, or when a disqualifier is found." />
          <Static label="Manual changes" value="Managers only, note required, labelled “Set by”." />
        </section>
      </div>

      <RecentlyHeard />

      <div className="save-bar">
        {save.error && <ErrorBox error={save.error} />}
        {dirty && problems[0] && <span className="footnote-text" style={{ color: 'var(--red-fg)' }}>{problems[0]}</span>}
        {dirty && !problems[0] && <span className="footnote-text">Unsaved changes. Counts update after saving.</span>}
        {dirty && (
          <button className="btn btn--secondary" onClick={() => setDirty(false)}>
            Discard
          </button>
        )}
        <button className="btn btn--accent" disabled={!dirty || problems.length > 0 || save.isPending} onClick={onSave}>
          {save.isPending ? 'Saving…' : 'Save journey'}
        </button>
      </div>
      {required.length === 0 && <span className="footnote-text">Every milestone is optional, so the funnel is empty.</span>}
    </div>
  );
}

function triggerLine(m: Draft, data: JourneySetupResponse): string {
  if (m.trigger === 'tag') return m.value ? `Tag added · ${m.value}` : 'Tag added · not set';
  if (m.trigger === 'appointment') return `Appointment showed · ${data.calendars.find((c) => c.id === m.value)?.name ?? 'calendar not set'}`;
  return `AI hears it · tag ${m.value || slug(m.name)}`;
}

function TriggerCard({ t, cur, onPick, title, help, children }: { t: MilestoneTrigger; cur: Draft; onPick: (t: MilestoneTrigger) => void; title: string; help: string; children: React.ReactNode }) {
  const on = cur.trigger === t;
  return (
    <div className={`trig${on ? ' is-on' : ''}`}>
      <button className="trig__head" onClick={() => !on && onPick(t)} role="radio" aria-checked={on}>
        <span className="trig__dot" />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{title}</span>
          <span className="card-meta">{help}</span>
        </span>
      </button>
      {on && <div className="trig__body">{children}</div>}
    </div>
  );
}

/** Searchable: accounts have hundreds of tags, and a new tag (no leads yet) would sit at the bottom of a list. */
function TagPicker({ value, tags, onChange }: { value: string; tags: JourneySetupResponse['tags']; onChange: (v: string) => void }) {
  const known = tags.find((t) => t.name.toLowerCase() === value.trim().toLowerCase());
  return (
    <>
      <input className="text-input" list="hl-tags" value={value} placeholder="Type to search your HighLevel tags…" onChange={(e) => onChange(e.target.value)} />
      <datalist id="hl-tags">
        {tags.map((t) => (
          <option key={t.name} value={t.name}>
            {t.leads} lead{t.leads === 1 ? '' : 's'}, 90 days
          </option>
        ))}
      </datalist>
      {value.trim() && (
        <span className="card-meta">
          {known
            ? `${known.leads.toLocaleString('en-US')} lead${known.leads === 1 ? '' : 's'} got this tag in the last 90 days.`
            : 'Not in HighLevel yet. New tags show up here within 3 minutes of being created; the milestone counts once leads get the tag.'}
        </span>
      )}
    </>
  );
}

/** What the AI counted lately across leads, so a manager can spot-check it. */
function RecentlyHeard() {
  const { data } = useAiMilestones();
  const me = useMe();
  if (!data?.length) return null;
  return (
    <section className="card stack-12">
      <div className="card-head">
        <span className="card-title">Heard by AI</span>
        <span className="card-meta">Counted automatically. Remove any the AI got wrong.</span>
      </div>
      <AiHeardList items={data} timeZone={me.data?.company.timeZone ?? 'UTC'} showLead />
    </section>
  );
}

function Static({ label, value }: { label: string; value: string }) {
  return (
    <div className="js-row">
      <span style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</span>
      <span className="card-meta" style={{ maxWidth: 300, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

/** Last 90 days: each required step relative to the first, and as a share of the previous required step. */
function Funnel({ list }: { list: Draft[] }) {
  const first = list.find((m) => !m.optional)?.leads ?? 0;
  const rows = useMemo(() => {
    let prev: number | null = null;
    return list.map((m) => {
      const out = { m, pctOfPrev: !m.optional && prev && m.leads != null ? Math.round((100 * m.leads) / prev) : null };
      if (!m.optional && m.leads != null) prev = m.leads;
      return out;
    });
  }, [list]);
  return (
    <section className="card stack-12">
      <div className="card-head">
        <span className="card-title">Funnel preview</span>
        <span className="card-meta">Last 90 days</span>
      </div>
      {rows.map(({ m, pctOfPrev }) => (
        <div key={m.key} className="funnel-row" style={{ opacity: m.optional ? 0.5 : 1 }}>
          <span className="funnel-row__name">{m.name}</span>
          <span className="funnel-bar">
            <span style={{ width: `${m.optional || !first || m.leads == null ? 0 : Math.max(2, (100 * m.leads) / first)}%` }} />
          </span>
          <span className="funnel-row__n">
            {m.leads == null ? '—' : m.leads.toLocaleString('en-US')}
            <span className="card-meta">{m.optional ? ' Optional, not in the funnel' : pctOfPrev != null ? ` ${pctOfPrev}% of previous` : ''}</span>
          </span>
        </div>
      ))}
    </section>
  );
}
